import { useEffect, useRef, useState } from "react";
import { searchPool } from "@/mocks/index";

const navLinks = [
  { label: "正在现场", href: "#now" },
  { label: "弹幕合集", href: "#danmaku" },
  { label: "主播锐评", href: "#review" },
  { label: "反应视频", href: "#video" },
  { label: "热议榜", href: "#ranking" },
  { label: "常见问题", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = query.trim()
    ? searchPool.filter((item) => item.title.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 6)
    : [];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-stone-950/85 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 md:px-6">
        <a href="#top" className="flex items-center gap-2.5 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500 text-white">
            <i className="ri-fire-fill text-lg" />
          </span>
          <span className="flex items-baseline gap-1.5">
            <span className="font-display text-xl font-bold tracking-tight text-white">99吃瓜</span>
            <span className="hidden rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-orange-300 sm:inline-block">
              LIVE 热议社区
            </span>
          </span>
        </a>

        <ul className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                className="whitespace-nowrap rounded-full px-3.5 py-2 text-sm text-stone-300 transition-colors hover:bg-white/5 hover:text-orange-300"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2.5">
          <div ref={boxRef} className="relative hidden sm:block">
            <div
              className="flex cursor-pointer items-center gap-2 rounded-full bg-white/5 px-3.5 py-2 text-sm text-stone-400 transition-colors hover:bg-white/10"
              onClick={() => setSearchOpen((v) => !v)}
            >
              <i className="ri-search-line" />
              <span>搜话题 / 主播</span>
            </div>
            {searchOpen && (
              <div className="absolute right-0 top-12 w-80 overflow-hidden rounded-2xl border border-white/10 bg-stone-900 shadow-2xl">
                <div className="border-b border-white/10 p-3">
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="输入关键词搜索…"
                    className="w-full bg-transparent text-sm text-white outline-none placeholder:text-stone-500"
                  />
                </div>
                <div className="max-h-72 overflow-auto p-2">
                  {query.trim() && results.length === 0 && (
                    <p className="px-3 py-4 text-center text-sm text-stone-500">暂时没有匹配的热点</p>
                  )}
                  {!query.trim() && (
                    <p className="px-3 py-4 text-center text-sm text-stone-500">试试搜“名场面”“连麦”“锐评”</p>
                  )}
                  {results.map((r) => (
                    <a
                      key={r.key}
                      href="#ranking"
                      onClick={() => {
                        setQuery("");
                        setSearchOpen(false);
                      }}
                      className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm text-stone-200 transition-colors hover:bg-white/5"
                    >
                      <span className="truncate">{r.title}</span>
                      <span className="whitespace-nowrap rounded-md bg-orange-500/15 px-2 py-0.5 text-[11px] text-orange-300">
                        {r.type}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a
            href="#now"
            className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-stone-950 transition-colors hover:bg-orange-400 md:inline-flex"
          >
            <span className="relative flex h-2 w-2">
              <span className="live-dot absolute inline-flex h-full w-full rounded-full bg-stone-950" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-stone-950" />
            </span>
            进现场陪看
          </a>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="切换菜单"
            className="flex h-10 w-10 items-center justify-center rounded-full text-xl text-stone-200 hover:bg-white/10 lg:hidden"
          >
            <i className={open ? "ri-close-line" : "ri-menu-line"} />
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-white/10 bg-stone-950/95 backdrop-blur-md lg:hidden">
          <ul className="space-y-1 px-4 py-4">
            {navLinks.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block whitespace-nowrap rounded-xl px-4 py-3 text-sm text-stone-200 hover:bg-white/5 hover:text-orange-300"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}